import { useEffect, useState } from 'react';
import { GOAL_AED, audienceIsVague, type Pitch } from '@studio/shared';
import * as api from '../api.js';

const EMPTY = { audience: '', hook: '', priceAed: 10, plan: '', peopleTheyKnow: 0 };

/**
 * Pitch Mode.
 *
 * Not a form for the sake of one — the arithmetic is the point. A child sets a
 * price, is shown immediately how many people would have to buy it, and then
 * compares that to how many people they could actually tell. That collision is
 * the real lesson: what limits a small business is distribution, not the idea.
 *
 * The verdict never flatters and never discourages. It states the consequence of
 * the number they chose and leaves the decision with them.
 */
export function PitchMode({ games }: { games: api.ArcadeGame[] }) {
  const [state, setState] = useState<api.PitchState | null>(null);
  const [gameId, setGameId] = useState<string>('');
  const [answers, setAnswers] = useState<Pitch>(EMPTY as Pitch);
  const [result, setResult] = useState<api.PitchResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getPitches().then(setState).catch(() => setState(null));
  }, []);

  const mine = games.filter((g) => g.isMine);

  if (!state) return <p>Loading…</p>;

  if (!state.unlocked) {
    return (
      <section className="pitch pitch--locked">
        <h2>Pitch Mode</h2>
        <p>
          Ship {state.needed} games and you can start pitching them — like on the telly, but to
          your family.
        </p>
        <p className="pitch__progress">
          {state.gamesShipped} of {state.needed} shipped
        </p>
      </section>
    );
  }

  const live = result?.ok ? result : null;

  return (
    <section className="pitch">
      <h2>Pitch one of your games</h2>

      <label className="field">
        <span className="field__q">Which game?</span>
        <select value={gameId} onChange={(e) => setGameId(e.target.value)}>
          <option value="">Choose one…</option>
          {mine.map((g) => (
            <option key={g.id} value={g.id}>
              {g.title}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="field__q">Who is it for?</span>
        <span className="field__hint">Not everyone. Who exactly?</span>
        <textarea
          rows={2}
          maxLength={200}
          value={answers.audience}
          onChange={(e) => setAnswers({ ...answers, audience: e.target.value })}
        />
        {audienceIsVague(answers.audience) && (
          <span className="pitch__push">
            “Everyone” is nobody. Who would like this more than most people would?
          </span>
        )}
      </label>

      <label className="field">
        <span className="field__q">What would you say to make them want it?</span>
        <span className="field__hint">One sentence.</span>
        <textarea
          rows={2}
          maxLength={200}
          value={answers.hook}
          onChange={(e) => setAnswers({ ...answers, hook: e.target.value })}
        />
      </label>

      <label className="field">
        <span className="field__q">
          What would you charge? <em>{answers.priceAed} AED</em>
        </span>
        <input
          type="range"
          min={0}
          max={50}
          step={1}
          value={answers.priceAed}
          onChange={(e) => setAnswers({ ...answers, priceAed: Number(e.target.value) })}
        />
      </label>

      <label className="field">
        <span className="field__q">
          Roughly how many people could you actually tell? <em>{answers.peopleTheyKnow}</em>
        </span>
        <span className="field__hint">Friends, class, cousins, neighbours.</span>
        <input
          type="range"
          min={0}
          max={200}
          step={5}
          value={answers.peopleTheyKnow}
          onChange={(e) => setAnswers({ ...answers, peopleTheyKnow: Number(e.target.value) })}
        />
      </label>

      <label className="field">
        <span className="field__q">What would you do with the money?</span>
        <textarea
          rows={2}
          maxLength={300}
          value={answers.plan}
          onChange={(e) => setAnswers({ ...answers, plan: e.target.value })}
        />
      </label>

      <button
        className="go"
        disabled={busy || !gameId || !answers.audience.trim() || !answers.hook.trim()}
        onClick={async () => {
          setBusy(true);
          try {
            setResult(await api.makePitch(gameId, answers));
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Working it out…' : 'Do the maths'}
      </button>

      {live && (
        <div className="pitch__result">
          <h3>
            To make {live.maths.goalAed} AED
            {live.maths.salesNeeded !== null && (
              <>
                {' '}
                you need <strong>{live.maths.salesNeeded}</strong> people to buy it
              </>
            )}
          </h3>
          <p>{live.verdict}</p>

          <table className="pitch__table">
            <thead>
              <tr>
                <th>If you charged</th>
                <th>You'd need</th>
              </tr>
            </thead>
            <tbody>
              {live.maths.atOtherPrices.map((row) => (
                <tr key={row.priceAed}>
                  <td>{row.priceAed} AED</td>
                  <td>{row.salesNeeded} buyers</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pitch__saved">
            Saved. Read it out on Shark Tank Friday — a grown-up can see it in their console.
          </p>
        </div>
      )}

      {state.pitches.length > 0 && (
        <>
          <h3>Your pitches so far</h3>
          <ul className="pitch__list">
            {state.pitches.map((p) => (
              <li key={p.id}>
                <strong>{p.title}</strong>
                <span>{new Date(p.at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="pitch__goal">The goal is {GOAL_AED} AED. Pick a game and see what it takes.</p>
    </section>
  );
}
